import { useEffect, useState, useCallback, memo } from "react"
import WithRouter from "../components/common/WithRouter"

import Header from "./Header"
import Sidebar from "./Sidebar"
import Footer from "./Footer"
import Breadcrumbs from "../components/common/Breadcrumbs"

import {
  changeLayout,
  changeSidebarTheme,
  changeLayoutMode,
  changeLayoutWidth,
  changeLayoutPosition,
  changeTopbarTheme,
  changeLeftsidebarSizeType,
  changeLeftsidebarViewType,
  changeSidebarImageType,
  changeSidebarVisibility,
} from "../slices/thunks"

import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import type { RootState } from "../store"
import { useAppDispatch } from "@/hooks/useAppDispatch"

export interface LayoutProps {
  children: React.ReactNode
}

const selectLayoutState = (state: RootState) => state.Layout

const selectLayoutProperties = createSelector(
  selectLayoutState,
  (layout) => ({
    layoutType: layout.layoutType,
    leftSidebarType: layout.leftSidebarType,
    layoutModeType: layout.layoutModeType,
    layoutWidthType: layout.layoutWidthType,
    layoutPositionType: layout.layoutPositionType,
    topbarThemeType: layout.topbarThemeType,
    leftsidbarSizeType: layout.leftsidbarSizeType,
    leftSidebarViewType: layout.leftSidebarViewType,
    leftSidebarImageType: layout.leftSidebarImageType,
    sidebarVisibilitytype: layout.sidebarVisibilitytype,
  })
)

const Layout = memo(({ children }: LayoutProps) => {
  const dispatch = useAppDispatch()
  const [headerClass, setHeaderClass] = useState("")

  const {
    layoutType,
    leftSidebarType,
    layoutModeType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftsidbarSizeType,
    leftSidebarViewType,
    leftSidebarImageType,
    sidebarVisibilitytype,
  } = useSelector(selectLayoutProperties)

  useEffect(() => {
    if (
      layoutType ||
      leftSidebarType ||
      layoutModeType ||
      layoutWidthType ||
      layoutPositionType ||
      topbarThemeType ||
      leftsidbarSizeType ||
      leftSidebarViewType ||
      leftSidebarImageType ||
      sidebarVisibilitytype
    ) {
      window.dispatchEvent(new Event("resize"))
      dispatch(changeLeftsidebarViewType(leftSidebarViewType))
      dispatch(changeLeftsidebarSizeType(leftsidbarSizeType))
      dispatch(changeSidebarTheme(leftSidebarType))
      dispatch(changeLayoutMode(layoutModeType))
      dispatch(changeLayoutWidth(layoutWidthType))
      dispatch(changeLayoutPosition(layoutPositionType))
      dispatch(changeTopbarTheme(topbarThemeType))
      dispatch(changeLayout(layoutType))
      dispatch(changeSidebarImageType(leftSidebarImageType))
      dispatch(changeSidebarVisibility(sidebarVisibilitytype))
    }
  }, [
    layoutType,
    leftSidebarType,
    layoutModeType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftsidbarSizeType,
    leftSidebarViewType,
    leftSidebarImageType,
    sidebarVisibilitytype,
    dispatch,
  ])

  const onChangeLayoutMode = useCallback((value: string) => {
    dispatch(changeLayoutMode(value))
  }, [dispatch])

  const scrollNavigation = useCallback(() => {
    const scrollup = document.documentElement.scrollTop
    setHeaderClass(scrollup > 50 ? "topbar-shadow" : "")
  }, [])

  useEffect(() => {
    window.addEventListener("scroll", scrollNavigation, { passive: true })
    return () => window.removeEventListener("scroll", scrollNavigation)
  }, [scrollNavigation])

  // Hamburger icon: "open" (X) when sidebar is collapsed (sm), so click expands
  useEffect(() => {
    const hamburger = document.querySelector(".hamburger-icon")
    if (!hamburger) return
    const isCollapsed = leftsidbarSizeType === "sm"
    if (layoutType === "vertical" || layoutType === "twocolumn") {
      if (isCollapsed) hamburger.classList.add("open")
      else hamburger.classList.remove("open")
    } else {
      hamburger.classList.toggle("open", sidebarVisibilitytype !== "show")
    }
  }, [sidebarVisibilitytype, layoutType, leftsidbarSizeType])

  return (
    <div id="layout-wrapper">
      <Header
        headerClass={headerClass}
        layoutModeType={layoutModeType}
        onChangeLayoutMode={onChangeLayoutMode}
        leftsidbarSizeType={leftsidbarSizeType}
        layoutType={layoutType}
      />
      <Sidebar layoutType={layoutType} />
      <div className="main-content">
        {/* Breadcrumbs + document.title una sola vez para todas las páginas */}
        <Breadcrumbs />
        {children}
        <Footer />
      </div>
    </div>
  )
})

Layout.displayName = 'Layout'

export default WithRouter(Layout)
